// @flow
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { WebsocketProvider } from 'y-websocket'
import { type YMap, Doc } from 'yjs'
import { getCanvasId } from '../connect/selectors'
import { usePrevious } from '../../../utility/hooks'
import { isController } from '../../../utility/urls'
import { useMissionServerUrls } from '../../../websockets/urls'
import { createTLStore, type TLStore } from '@tldraw/tldraw'
import { shapes } from '../customShapes'

export type DocumentData = {
	id: string,
	doc: Doc,
	shapes: YMap<string, { id: string }>,
}

export type Identity = {|
	missionCode: string,
	// If studentId is not set, attempt to connect as a teacher
	studentId?: ?string,
	roomId: string,
|}

// Items of which we only need one per document.
type OnePerDocumentItems = {
	webSocketProvider: WebsocketProvider,
	documentData: DocumentData,
	store: TLStore,
}

type WebSocketDocumentMap = {
	[documentId: string]: OnePerDocumentItems,
}

type Context = {
	getDoc: Identity => DocumentData,
	webSocketsRef: { current: WebSocketDocumentMap },
}

const CreativeCanvasDocumentContext = React.createContext<Context | void>()
const MAX_BACK_OFF_TIME = 10000 // 10 seconds
const MAX_RETRY_ATTEMPTS = 3
/**
 * Handles websocket connection for showing multiple canvases in the player at the same time.
 */
export function CreativeCanvasDocumentProvider({ children }: { children: React$Node }): React$Node {
	const _docs = React.useRef<{ [documentId: string]: DocumentData }>({})

	const webSocketsRef = React.useRef<WebSocketDocumentMap>({})

	/**
	 * Gets doc data for a specific document id. Only will return an
	 * un-truthy value if the CanvasConnectionProvider is not initialized.
	 * @param {string}
	 */
	const getDoc = React.useCallback((identity: Identity) => {
		const { roomId: id } = identity
		if (_docs.current && _docs.current[id]) {
			return _docs.current[id]
		}
		const doc = new Doc()
		const shapes = doc.getMap('shapes')
		_docs.current[id] = {
			id,
			doc,
			shapes,
		}
		return _docs.current[id]
	}, [])

	useCleanUpWebsocketsOnCreativeCanvasOver(webSocketsRef)

	// Clean up web sockets on unmount and/or page unload
	React.useEffect(() => {
		function handleDisconnect() {
			Object.keys(webSocketsRef.current).forEach(websocketId => {
				webSocketsRef.current[websocketId]?.webSocketProvider.destroy()
			})
			webSocketsRef.current = {}
		}
		window.addEventListener('beforeunload', handleDisconnect)
		return () => {
			handleDisconnect()
			window.removeEventListener('beforeunload', handleDisconnect)
		}
	}, [])

	return (
		<CreativeCanvasDocumentContext.Provider value={{ getDoc, webSocketsRef }}>
			{children}
		</CreativeCanvasDocumentContext.Provider>
	)
}

/**
 * Gets all creative canvas items which we only need one of per document. This includes the yjs websocket, the yjs document, and the tldraw store.
 * @param identity identifiers to determine which creative canvas document is needed to get; includes missionCode, studentId, and roomId
 */
export function useOnePerDocumentItems(
	identity: Identity
): { onePerDocumentItems: OnePerDocumentItems | null, error: Error | null } {
	const context = useContext(CreativeCanvasDocumentContext)
	if (!context) {
		throw new Error('Cannot access yjs websocket without CreativeCanvasDocumentProvider')
	}
	const { webSocketsRef, getDoc } = context
	const websocketUrl = useMissionServerUrls()[0].data?.websocketUrl
	const isTeacher = isController()

	const [error, setError] = useState<Error | null>(null)
	const [webSocketIsDestroyed, setWebSocketIsDestroyed] = useState(false)

	// In a typical useMemo, the result would be derived from the dependencies. In this case, the result comes directly from the webSocketsRef,
	// which means that there is a minor concern about keeping the returned value of `useOnePerDocumentItems` up to date with what is actually in webSocketsRef.
	// Keeping websockets in state would require us to keep both the `webSocketsRef` and a `webSockets` state variable in the CreativeCanvasDocumentProvider,
	// along with some other complications, which is why I opted to only use a ref.
	const onePerDocumentItems = useMemo(() => {
		if (webSocketIsDestroyed) {
			return null
		}
		if (webSocketsRef.current[identity.roomId]) {
			return webSocketsRef.current[identity.roomId]
		}
		if (!websocketUrl) {
			throw new Error('Cannot connect websocket without websocket url')
		}
		const documentData = getDoc(identity)

		const queryParams: {| missionCode: string, studentId?: string, isTeacher?: string |} = {
			missionCode: identity.missionCode,
		}
		if (isTeacher) {
			queryParams.isTeacher = 'true'
		} else if (identity.studentId) {
			queryParams.studentId = identity.studentId
		}

		const webSocketProvider = new WebsocketProvider(
			websocketUrl + '-document-collaboration',
			identity.roomId,
			documentData.doc,
			{
				params: queryParams,
				// make the initial websocket connection automatically
				connect: true,
				maxBackoffTime: MAX_BACK_OFF_TIME, // 10 seconds
			}
		)

		// Catch on message error
		const originalOnMessage = webSocketProvider?.ws?.onmessage
		if (webSocketProvider?.ws?.onmessage && originalOnMessage) {
			webSocketProvider.ws.onmessage = function(event) {
				try {
					originalOnMessage(event)
				} catch (error) {
					// By setting an error here, we are able to display an error message to the user through the canvas and TlDraw throws the error for sentry to catch.
					setError(error)
				}
			}
		}

		webSocketProvider.on('connection-error', (error: Error) => {
			if (webSocketProvider.wsUnsuccessfulReconnects > MAX_RETRY_ATTEMPTS) {
				webSocketProvider.shouldConnect = false
				webSocketProvider.destroy()
				delete webSocketsRef.current[identity.roomId]
				// Previously, we had a piece of state in the CreativeCanvasDocumentProvider that held all websockets and their statuses, and was synced
				// with the webSocketsRef. Putting websockets in state like that could work to manage destroyed websockets, but it would be more difficult to
				// manage. We have to have some piece of state to cause a rerender so the useMemo will run, so we just use `webSocketIsDestroyed` to trigger that.
				setWebSocketIsDestroyed(true)
			} else {
				setError(error)
			}
		})
		webSocketsRef.current[identity.roomId] = {
			webSocketProvider: webSocketProvider,
			documentData,
			store: createTLStore({ shapes: shapes }),
		}

		return webSocketsRef.current[identity.roomId]
	}, [identity, webSocketsRef, getDoc, websocketUrl, isTeacher, webSocketIsDestroyed])

	return {
		onePerDocumentItems,
		error,
	}
}

/**
 * A hook that returns true if there was a creative canvas but now there is not.
 * @returns {boolean}
 */
function useIsCreativeCanvasOver() {
	const canvasId = useSelector(getCanvasId)
	const previousCanvasId = usePrevious(canvasId)

	if (!canvasId && previousCanvasId) {
		return true
	}
}

/**
 * Destroys websockets when the creative canvas is over. Does not destroy websockets that are being used for exporting.
 */
function useCleanUpWebsocketsOnCreativeCanvasOver(webSocketsRef: {
	current: WebSocketDocumentMap,
}) {
	const shouldCleanUp = useIsCreativeCanvasOver()
	useEffect(() => {
		if (shouldCleanUp) {
			Object.keys(webSocketsRef.current).forEach(websocketId => {
				webSocketsRef.current[websocketId].webSocketProvider.destroy()
				delete webSocketsRef.current[websocketId]
			})
		}
	}, [shouldCleanUp, webSocketsRef])
}
